import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Badge,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	Input,
	Label,
	Textarea,
	AlertDialogTrigger,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
	Select as _Select,
	SelectTrigger as _SelectTrigger,
	SelectContent as _SelectContent,
	SelectItem as _SelectItem,
	SelectValue as _SelectValue,
} from "@/components/primitives";
import {
	ArrowLeft,
	CheckCircle,
	AlertTriangle,
	Target,
	GripVertical,
	Clock,
	TrendingUp,
	Activity,
	FileText,
	PlayCircle,
	Trash2,
	Archive,
	CalendarPlus,
	Plus,
	Filter,
	Eye,
	EyeOff,
	ChevronDown,
	ChevronUp,
	Menu,
	X,
	Edit,
	Save,
} from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useCorrectiveActions } from "./hooks/useCorrectiveActions";
import { useActionManagement } from "./hooks/useActionManagement";
import { useActionArchive } from "./hooks/useActionArchive";
import type { CorrectiveAction } from "./types";
import { calculateMaxPriority } from "./utils";

export function AllCorrectiveActions() {
	const { user } = useAuth();

	// Data hooks
	const {
		correctiveActions,
		activeReflections,
		loading,
		refetch,
		setCorrectiveActions,
	} = useCorrectiveActions(user?.id);

	const { archivedActionIds, archiveAction, unarchiveAction, isArchived } =
		useActionArchive();

	const {
		updating,
		createAction,
		updateStatus,
		updatePriority,
		updateAction,
		deleteAction,
	} = useActionManagement({
		userId: user?.id,
		correctiveActions,
		refetch,
	});

	// UI State
	const [expandedActions, setExpandedActions] = useState<Set<string>>(
		new Set(),
	);
	const [manuallyExpandedActions, setManuallyExpandedActions] = useState<
		Set<string>
	>(new Set());
	const [showCompleted, setShowCompleted] = useState(true); // Show completed by default
	const [showArchived, setShowArchived] = useState(false);
	const [showAll, setShowAll] = useState(false);
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [visibleCounts, setVisibleCounts] = useState<{
		inProgress: number;
		notStarted: number;
		completed: number;
	}>({
		inProgress: 3,
		notStarted: 3,
		completed: 3,
	});
	const [newActionTitle, setNewActionTitle] = useState("");
	const [newActionDescription, setNewActionDescription] = useState("");
	const [selectedReflectionIds, setSelectedReflectionIds] = useState<string[]>(
		[],
	);
	const draggedItem = useRef<string | null>(null);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [isDraggingOverTopPriority, setIsDraggingOverTopPriority] =
		useState(false);

	// Reminder dialog state
	const [openForAction, setOpenForAction] = useState<string | null>(null);
	const [eventType, setEventType] = useState<"one-off" | "recurring">(
		"one-off",
	);
	const [recurrenceType, setRecurrenceType] = useState<"daily" | "weekly">(
		"daily",
	);
	const [weeklyDay, setWeeklyDay] = useState<number>(new Date().getDay());
	const [date, setDate] = useState<string>(() =>
		new Date().toISOString().slice(0, 10),
	);
	const [time, setTime] = useState<string>(() => {
		const d = new Date();
		d.setMinutes(0);
		d.setHours(d.getHours() + 1);
		return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
	});
	const [duration, setDuration] = useState<number>(30);
	const [eventTitle, setEventTitle] = useState<string>("");

	// Editing state
	const [editingActionId, setEditingActionId] = useState<string | null>(null);
	const [editTitle, setEditTitle] = useState<string>("");
	const [editDescription, setEditDescription] = useState<string>("");

	const toggleExpanded = (actionId: string) => {
		setExpandedActions((prev) => {
			const next = new Set(prev);
			if (next.has(actionId)) {
				next.delete(actionId);
				// Remove from manually expanded if closing
				setManuallyExpandedActions((manually) => {
					const newManually = new Set(manually);
					newManually.delete(actionId);
					return newManually;
				});
			} else {
				next.add(actionId);
				// Mark as manually expanded when using the button
				setManuallyExpandedActions((manually) =>
					new Set(manually).add(actionId),
				);
			}
			return next;
		});
	};

	const handleContainerClick = (actionId: string, e: React.MouseEvent) => {
		// Don't expand if clicking on interactive elements
		const target = e.target as HTMLElement;
		if (
			target.closest("button") ||
			target.closest("select") ||
			target.closest('[role="button"]') ||
			target.closest("a")
		) {
			return;
		}

		setExpandedActions((prev) => {
			const next = new Set(prev);
			const isCurrentlyExpanded = next.has(actionId);

			if (isCurrentlyExpanded) {
				// If clicking on an already expanded item, close it (unless manually expanded)
				if (!manuallyExpandedActions.has(actionId)) {
					next.delete(actionId);
				}
			} else {
				// Expand the clicked item
				next.add(actionId);

				// Close other expanded items that weren't manually expanded
				const toClose = Array.from(prev).filter(
					(id) => id !== actionId && !manuallyExpandedActions.has(id),
				);
				toClose.forEach((id) => next.delete(id));
			}

			return next;
		});
	};

	const getStatusIcon = (action: CorrectiveAction) => {
		if (isArchived(action.id))
			return <Archive className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
		switch (action.status) {
			case "completed":
				return (
					<CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
				);
			case "in_progress":
				return (
					<PlayCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
				);
			default:
				return <Clock className="h-4 w-4 text-gray-400 dark:text-gray-500" />;
		}
	};

	const getStatusColor = (action: CorrectiveAction) => {
		if (isArchived(action.id))
			return "bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700";
		switch (action.status) {
			case "completed":
				return "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800";
			case "in_progress":
				return "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800";
			default:
				return "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800";
		}
	};

	const getStatusText = (action: CorrectiveAction) =>
		isArchived(action.id) ? "archived" : action.status.replace("_", " ");

	const selectedAction = useMemo(
		() => correctiveActions.find((a) => a.id === openForAction) || null,
		[openForAction, correctiveActions],
	);

	const filteredActions = useMemo(() => {
		let items = correctiveActions;
		// Filter by archived status
		if (!showArchived) {
			items = items.filter((a) => !isArchived(a.id));
		}
		// Filter by completed status
		if (!showCompleted) {
			items = items.filter((a) => a.status !== "completed");
		}
		return items;
	}, [correctiveActions, showCompleted, showArchived, isArchived]);

	// Extract top priority (priority 1) action
	const topPriorityAction = useMemo(() => {
		return filteredActions.find((a) => a.priority === 1) || null;
	}, [filteredActions]);

	// Expand top priority action by default on mount or when it changes
	useEffect(() => {
		if (
			topPriorityAction &&
			!expandedActions.has(topPriorityAction.id) &&
			!manuallyExpandedActions.has(topPriorityAction.id)
		) {
			setExpandedActions((prev) => new Set(prev).add(topPriorityAction.id));
		}
	}, [topPriorityAction?.id]);

	// Expand top priority action by default on mount or when it changes
	useEffect(() => {
		if (topPriorityAction && !expandedActions.has(topPriorityAction.id)) {
			setExpandedActions((prev) => new Set(prev).add(topPriorityAction.id));
		}
	}, [topPriorityAction?.id]);

	// Group actions by status: in_progress first, then not_started, then completed
	// Maintain priority order within each group
	// Exclude top priority action from grouped actions
	const groupedActions = useMemo(() => {
		const actionsWithoutTop = filteredActions.filter((a) => a.priority !== 1);
		const inProgress = actionsWithoutTop
			.filter((a) => a.status === "in_progress")
			.sort((a, b) => a.priority - b.priority);
		const notStarted = actionsWithoutTop
			.filter((a) => a.status === "not_started")
			.sort((a, b) => a.priority - b.priority);
		const completed = actionsWithoutTop
			.filter((a) => a.status === "completed")
			.sort((a, b) => a.priority - b.priority);
		return [...inProgress, ...notStarted, ...completed];
	}, [filteredActions]);

	const completedCount = useMemo(
		() =>
			correctiveActions.filter(
				(a) => a.status === "completed" && !isArchived(a.id),
			).length,
		[correctiveActions, isArchived],
	);
	const archivedCount = useMemo(
		() => archivedActionIds.size,
		[archivedActionIds],
	);

	// Reset visible counts when filters change
	useEffect(() => {
		setVisibleCounts({
			inProgress: 3,
			notStarted: 3,
			completed: 3,
		});
	}, [showCompleted, showArchived]);

	const handleDragStart = (e: React.DragEvent, id: string) => {
		draggedItem.current = id;
		e.dataTransfer.effectAllowed = "move";
		e.dataTransfer.setData("text/plain", id);
	};
	const handleDragEnd = () => {
		draggedItem.current = null;
	};
	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		e.dataTransfer.dropEffect = "move";
	};
	const handleDrop = async (e: React.DragEvent, targetId: string) => {
		e.preventDefault();
		e.stopPropagation();
		const sourceId = draggedItem.current;
		if (!sourceId || sourceId === targetId) {
			draggedItem.current = null;
			return;
		}

		const all = [...correctiveActions];
		const from = all.findIndex((a) => a.id === sourceId);
		const to = all.findIndex((a) => a.id === targetId);
		if (from === -1 || to === -1) {
			draggedItem.current = null;
			return;
		}

		const [moved] = all.splice(from, 1);
		all.splice(to, 0, moved);
		const reindexed = all.map((a, i) => ({ ...a, priority: i + 1 }));
		setCorrectiveActions(reindexed);
		try {
			await Promise.all(reindexed.map((a) => updatePriority(a.id, a.priority)));
		} finally {
			draggedItem.current = null;
		}
	};

	// Handle setting a new top priority
	const handleSetTopPriority = async (actionId: string) => {
		const action = correctiveActions.find((a) => a.id === actionId);
		if (!action) return;

		const currentTopPriority = correctiveActions.find((a) => a.priority === 1);
		if (currentTopPriority && currentTopPriority.id === actionId) return; // Already top priority

		try {
			// Get all actions sorted by priority
			const allActions = [...correctiveActions].sort(
				(a, b) => a.priority - b.priority,
			);

			// Remove the action that will become top priority
			const actionToPromote = allActions.find((a) => a.id === actionId);
			if (!actionToPromote) return;

			const remainingActions = allActions.filter((a) => a.id !== actionId);

			// If there was a previous top priority, it becomes priority 2
			// All other actions shift down by 1
			const reindexed = remainingActions.map((a, i) => ({
				...a,
				priority: i + 2, // Start from 2 (since 1 will be the new top priority)
			}));

			// Add the new top priority at the beginning
			reindexed.unshift({ ...actionToPromote, priority: 1 });

			// Update all priorities
			setCorrectiveActions(reindexed);
			await Promise.all(reindexed.map((a) => updatePriority(a.id, a.priority)));
		} catch (error) {
			console.error("Error setting top priority:", error);
		}
	};

	// Handle dropping on top priority section
	const handleTopPriorityDrop = async (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDraggingOverTopPriority(false);
		const sourceId = draggedItem.current;
		if (!sourceId) {
			draggedItem.current = null;
			return;
		}

		await handleSetTopPriority(sourceId);
		draggedItem.current = null;
	};

	const handleTopPriorityDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		e.dataTransfer.dropEffect = "move";
		setIsDraggingOverTopPriority(true);
	};

	const handleTopPriorityDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDraggingOverTopPriority(false);
	};

	const toggleReflectionSelection = (reflectionId: string) => {
		setSelectedReflectionIds((prev) =>
			prev.includes(reflectionId)
				? prev.filter((id) => id !== reflectionId)
				: [...prev, reflectionId],
		);
	};

	const submitCreateAction = useCallback(async () => {
		await createAction({
			title: newActionTitle,
			description: newActionDescription,
			selectedReflectionIds,
		});
		setNewActionTitle("");
		setNewActionDescription("");
		setSelectedReflectionIds([]);
		setShowCreateForm(false);
	}, [
		createAction,
		newActionTitle,
		newActionDescription,
		selectedReflectionIds,
	]);

	// Render Action Function - Defined outside IIFE for reuse
	const renderAction = (
		action: CorrectiveAction,
		isTopPriority: boolean = false,
	) => {
		const isCompleted = action.status === "completed";
		const isInProgress = action.status === "in_progress";
		const isArchivedAction = isArchived(action.id);

		return (
			<div
				key={action.id}
				className={`group relative flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
					isTopPriority
						? "bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-300 dark:border-purple-700 shadow-md"
						: isArchivedAction
							? "opacity-60 bg-muted/30 dark:bg-muted/20 border-muted dark:border-muted"
							: isCompleted
								? "bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800"
								: isInProgress
									? "bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800"
									: "bg-background border-border hover:border-primary/50 hover:shadow-sm"
				}`}
				draggable={!isTopPriority}
				onDragStart={(e) => !isTopPriority && handleDragStart(e, action.id)}
				onDragEnd={handleDragEnd}
				onDragOver={handleDragOver}
				onDrop={(e) => handleDrop(e, action.id)}
				onClick={(e) => handleContainerClick(action.id, e)}
			>
				{/* Priority Number */}
				<div className="flex-shrink-0 flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 dark:bg-primary/20 text-primary font-bold text-xs sm:text-sm mt-0.5">
					{action.priority}
				</div>

				{/* Drag Handle - Always visible on mobile, hover on desktop - Hidden for top priority */}
				{!isTopPriority && (
					<div
						className="cursor-grab active:cursor-grabbing flex-shrink-0 mt-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
						title="Drag to reorder"
					>
						<GripVertical className="h-4 w-4 text-muted-foreground" />
					</div>
				)}

				{/* Task Content */}
				<div className="flex-1 min-w-0">
					<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
						<div className="flex-1 min-w-0">
							{/* Title */}
							{editingActionId === action.id ? (
								<div className="space-y-2 mb-2">
									<Input
										value={editTitle}
										onChange={(e) => setEditTitle(e.target.value)}
										placeholder="Enter title..."
										className="font-semibold text-base"
										onKeyDown={(e) => {
											if (e.key === "Enter" && !e.shiftKey) {
												e.preventDefault();
												const titleInput = e.currentTarget;
												const descInput =
													titleInput.parentElement?.querySelector(
														"textarea",
													) as HTMLTextAreaElement;
												if (descInput) {
													descInput.focus();
												}
											}
										}}
									/>
									<Textarea
										value={editDescription}
										onChange={(e) => setEditDescription(e.target.value)}
										placeholder="Enter description..."
										className="min-h-[80px] resize-y"
										rows={3}
									/>
									<div className="flex items-center gap-2">
										<Button
											size="sm"
											onClick={async (e) => {
												e.stopPropagation();
												if (editTitle.trim()) {
													await updateAction(action.id, {
														title: editTitle,
														description: editDescription,
													});
													setEditingActionId(null);
													setEditTitle("");
													setEditDescription("");
												}
											}}
											disabled={updating || !editTitle.trim()}
											className="h-7 text-xs"
										>
											<Save className="h-3 w-3 mr-1" />
											Save
										</Button>
										<Button
											size="sm"
											variant="outline"
											onClick={(e) => {
												e.stopPropagation();
												setEditingActionId(null);
												setEditTitle("");
												setEditDescription("");
											}}
											disabled={updating}
											className="h-7 text-xs"
										>
											Cancel
										</Button>
									</div>
								</div>
							) : (
								<>
									<h3
										className={`font-semibold text-base mb-1 ${
											isCompleted
												? "line-through text-muted-foreground"
												: "text-foreground"
										}`}
									>
										{action.title}
									</h3>

									{/* Description */}
									{action.description && (
										<p
											className={`text-sm mb-2 ${
												isCompleted
													? "text-muted-foreground line-through"
													: "text-muted-foreground"
											}`}
										>
											{action.description}
										</p>
									)}
								</>
							)}

							{/* Metadata */}
							<div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-muted-foreground">
								{action.reflection?.title && (
									<span className="flex items-center gap-1">
										<FileText className="h-3 w-3" />
										{action.reflection.title}
									</span>
								)}
								<span>Created {formatDate(action.created_at)}</span>
								{!isArchivedAction && (
									<Badge
										variant="outline"
										className={`text-xs ${
											isCompleted
												? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700"
												: isInProgress
													? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700"
													: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700"
										}`}
									>
										{getStatusIcon(action)}
										<span className="ml-1 capitalize">
											{getStatusText(action)}
										</span>
									</Badge>
								)}
								{isArchivedAction && (
									<Badge
										variant="outline"
										className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700"
									>
										<Archive className="h-3 w-3 mr-1" />
										Archived
									</Badge>
								)}
							</div>
						</div>

						{/* Action Buttons - Visible on mobile, hover on desktop */}
						<div className="flex items-center gap-1 flex-shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
							{editingActionId !== action.id && (
								<Button
									variant="ghost"
									size="sm"
									className="h-8 w-8 p-0"
									onClick={(e) => {
										e.stopPropagation();
										setEditingActionId(action.id);
										setEditTitle(action.title);
										setEditDescription(action.description || "");
									}}
									title="Edit task"
								>
									<Edit className="h-4 w-4" />
								</Button>
							)}
							{editingActionId !== action.id && (
								<>
									{!isTopPriority && !isArchivedAction && (
										<Button
											variant="outline"
											size="sm"
											className="h-8 text-xs"
											onClick={(e) => {
												e.stopPropagation();
												handleSetTopPriority(action.id);
											}}
											disabled={updating}
											title="Set as top priority"
										>
											<Target className="h-3 w-3 mr-1" />
											Top
										</Button>
									)}
									{!isArchivedAction && (
										<Select
											value={action.status}
											onValueChange={(value: string) => {
												if (value === "top_priority") {
													handleSetTopPriority(action.id);
												} else {
													updateStatus(
														action.id,
														value as CorrectiveAction["status"],
													);
												}
											}}
											disabled={updating}
										>
											<SelectTrigger
												className="w-32 sm:w-36 h-8 text-xs"
												onMouseDown={(e) => e.stopPropagation()}
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{!isTopPriority && (
													<SelectItem value="top_priority">
														<div className="flex items-center gap-2">
															<Target className="h-3 w-3 text-purple-600 dark:text-purple-400" />
															Top priority
														</div>
													</SelectItem>
												)}
												<SelectItem value="not_started">Not Started</SelectItem>
												<SelectItem value="in_progress">In Progress</SelectItem>
												<SelectItem value="completed">Completed</SelectItem>
											</SelectContent>
										</Select>
									)}

									<Button
										variant="ghost"
										size="sm"
										className="h-8 w-8 p-0"
										onClick={(e) => {
											e.stopPropagation();
											setOpenForAction(action.id);
											setEventTitle(action.title);
										}}
										title="Set reminder"
									>
										<CalendarPlus className="h-4 w-4" />
									</Button>

									<Button
										variant="ghost"
										size="sm"
										className="h-8 w-8 p-0"
										onClick={(e) => {
											e.stopPropagation();
											toggleExpanded(action.id);
										}}
										title="Toggle details"
									>
										{expandedActions.has(action.id) ? (
											<ChevronUp className="h-4 w-4" />
										) : (
											<ChevronDown className="h-4 w-4" />
										)}
									</Button>

									{!isArchivedAction && (
										<AlertDialog>
											<AlertDialogTrigger asChild>
												<Button
													variant="ghost"
													size="sm"
													className="h-8 w-8 p-0"
													onClick={(e) => e.stopPropagation()}
													title="Archive"
												>
													<Archive className="h-4 w-4" />
												</Button>
											</AlertDialogTrigger>
											<AlertDialogContent>
												<AlertDialogHeader>
													<AlertDialogTitle>Archive Action</AlertDialogTitle>
													<AlertDialogDescription>
														Are you sure you want to archive "{action.title}"?
														Archived actions can be restored later.
													</AlertDialogDescription>
												</AlertDialogHeader>
												<AlertDialogFooter>
													<AlertDialogCancel>Cancel</AlertDialogCancel>
													<AlertDialogAction
														onClick={() => archiveAction(action.id)}
													>
														Archive
													</AlertDialogAction>
												</AlertDialogFooter>
											</AlertDialogContent>
										</AlertDialog>
									)}

									{isArchivedAction && (
										<Button
											variant="ghost"
											size="sm"
											className="h-8 w-8 p-0"
											onClick={(e) => {
												e.stopPropagation();
												unarchiveAction(action.id);
											}}
											disabled={updating}
											title="Unarchive"
										>
											<Archive className="h-4 w-4" />
										</Button>
									)}

									<AlertDialog>
										<AlertDialogTrigger asChild>
											<Button
												variant="ghost"
												size="sm"
												className="h-8 w-8 p-0 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
												onClick={(e) => e.stopPropagation()}
												title="Delete"
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</AlertDialogTrigger>
										<AlertDialogContent>
											<AlertDialogHeader>
												<AlertDialogTitle>Delete Action</AlertDialogTitle>
												<AlertDialogDescription>
													Are you sure you want to permanently delete "
													{action.title}"? This action cannot be undone.
												</AlertDialogDescription>
											</AlertDialogHeader>
											<AlertDialogFooter>
												<AlertDialogCancel>Cancel</AlertDialogCancel>
												<AlertDialogAction
													onClick={() => deleteAction(action.id)}
													className="bg-red-600 hover:bg-red-700"
												>
													Delete
												</AlertDialogAction>
											</AlertDialogFooter>
										</AlertDialogContent>
									</AlertDialog>
								</>
							)}
						</div>
					</div>

					{/* Expanded Details */}
					{expandedActions.has(action.id) && (
						<div className="mt-4 pt-4 border-t border-border">
							<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
								<div>
									<h4 className="font-medium text-sm mb-2 flex items-center">
										<FileText className="h-4 w-4 mr-1" />
										Reflection Context
									</h4>
									<div className="bg-muted dark:bg-muted/50 p-3 rounded-lg">
										<h5 className="font-medium text-sm mb-1">
											{action.reflection?.title}
										</h5>
										<p className="text-xs text-muted-foreground">
											{action.reflection?.description?.slice(0, 150)}
											{action.reflection?.description &&
											action.reflection.description.length > 150
												? "..."
												: ""}
										</p>
									</div>
								</div>

								{action.signals && action.signals.length > 0 && (
									<div>
										<h4 className="font-medium text-sm mb-2 flex items-center">
											<Activity className="h-4 w-4 mr-1" />
											Related Signals ({action.signals.length})
										</h4>
										<div className="space-y-2">
											{action.signals.slice(0, 3).map((signal) => (
												<div
													key={signal.id}
													className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 p-2 rounded text-xs"
												>
													<div className="font-medium text-orange-900 dark:text-orange-200">
														{signal.title}
													</div>
													<div className="text-orange-700 dark:text-orange-300 mt-1">
														{signal.description?.slice(0, 80)}...
													</div>
												</div>
											))}
											{action.signals.length > 3 && (
												<div className="text-xs text-muted-foreground">
													+{action.signals.length - 3} more signals
												</div>
											)}
										</div>
									</div>
								)}

								{action.contributing_factors &&
									action.contributing_factors.length > 0 && (
										<div>
											<h4 className="font-medium text-sm mb-2 flex items-center">
												<AlertTriangle className="h-4 w-4 mr-1" />
												Contributing Factors (
												{action.contributing_factors.length})
											</h4>
											<div className="space-y-2">
												{action.contributing_factors
													.slice(0, 3)
													.map((factor) => (
														<div
															key={factor.id}
															className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-2 rounded text-xs"
														>
															<div className="font-medium text-red-900 dark:text-red-200">
																{factor.title}
															</div>
															<div className="text-red-700 dark:text-red-300 mt-1">
																{factor.description?.slice(0, 80)}...
															</div>
														</div>
													))}
												{action.contributing_factors.length > 3 && (
													<div className="text-xs text-muted-foreground">
														+{action.contributing_factors.length - 3} more
														factors
													</div>
												)}
											</div>
										</div>
									)}
							</div>
						</div>
					)}
				</div>
			</div>
		);
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
				<div className="container mx-auto px-4 py-6 sm:py-8 max-w-7xl">
					<div className="flex items-center justify-center min-h-[400px]">
						<div className="text-center">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
							<p className="text-muted-foreground">
								Loading corrective actions...
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
			<div className="container mx-auto px-4 py-6 sm:py-8 max-w-7xl">
				{/* Header */}
				<div className="mb-8">
					<div className="flex items-center justify-between gap-4">
						<div className="flex items-center gap-3">
							<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
								<Target className="h-5 w-5 text-white" />
							</div>
							<div>
								<h1 className="text-xl sm:text-2xl font-semibold">
									Corrective Actions
								</h1>
								<p className="text-muted-foreground">
									Manage and prioritize your action items
								</p>
							</div>
						</div>

						<div className="flex items-center gap-2">
							{/* New Action Button - Desktop */}
							<Button
								variant="outline"
								size="sm"
								onClick={() => setShowCreateForm(true)}
								className="hidden sm:flex items-center"
							>
								<Plus className="h-4 w-4 mr-1" />
								New Action
							</Button>

							{/* Mobile Menu Button */}
							<Button
								variant="outline"
								size="sm"
								className="sm:hidden"
								onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
							>
								{mobileMenuOpen ? (
									<X className="h-4 w-4" />
								) : (
									<Menu className="h-4 w-4" />
								)}
							</Button>
						</div>
					</div>
				</div>

				{/* Mobile Menu - Collapsible */}
				{mobileMenuOpen && (
					<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm mb-6 sm:hidden">
						<CardContent className="p-4 space-y-3">
							<Button
								variant="outline"
								size="sm"
								onClick={() => {
									setShowCreateForm(true);
									setMobileMenuOpen(false);
								}}
								className="w-full"
							>
								<Plus className="h-4 w-4 mr-2" />
								New Action
							</Button>

							<div className="border-t pt-3 space-y-2">
								<div className="flex items-center space-x-2 mb-2">
									<Filter className="h-4 w-4 text-muted-foreground" />
									<span className="text-sm font-medium">Filters</span>
								</div>
								<Button
									variant="secondary"
									size="sm"
									onClick={() => setShowCompleted(!showCompleted)}
									className="w-full justify-start"
								>
									{showCompleted ? (
										<EyeOff className="h-4 w-4 mr-2" />
									) : (
										<Eye className="h-4 w-4 mr-2" />
									)}
									{showCompleted ? "Hide" : "Show"} Completed ({completedCount})
								</Button>
								<Button
									variant={showArchived ? "default" : "outline"}
									size="sm"
									onClick={() => setShowArchived(!showArchived)}
									className="w-full justify-start"
								>
									{showArchived ? (
										<EyeOff className="h-4 w-4 mr-2" />
									) : (
										<Eye className="h-4 w-4 mr-2" />
									)}
									{showArchived ? "Hide" : "Show"} Archived ({archivedCount})
								</Button>
							</div>

							<div className="border-t pt-3">
								<Button
									variant="ghost"
									asChild
									className="w-full justify-start"
								>
									<Link
										to="/"
										className="flex items-center"
										onClick={() => setMobileMenuOpen(false)}
									>
										<ArrowLeft className="h-4 w-4 mr-2" />
										Back to Home
									</Link>
								</Button>
								<div className="text-xs text-muted-foreground mt-2 text-center">
									Showing{" "}
									{visibleCounts.inProgress +
										visibleCounts.notStarted +
										visibleCounts.completed}{" "}
									of {groupedActions.length} actions
								</div>
							</div>
						</CardContent>
					</Card>
				)}

				{/* Desktop Filters */}
				<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm mb-6 hidden sm:block">
					<CardContent className="p-4">
						<div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
							<div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
								<Button
									variant="ghost"
									asChild
									className="self-start w-full sm:w-auto"
								>
									<Link to="/" className="flex items-center">
										<ArrowLeft className="h-4 w-4 mr-2" />
										Back to Home
									</Link>
								</Button>
								<div className="flex items-center space-x-2">
									<Filter className="h-4 w-4 text-muted-foreground" />
									<span className="text-sm font-medium">Filters:</span>
								</div>
								<div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
									<Button
										variant="secondary"
										size="sm"
										onClick={() => setShowCompleted(!showCompleted)}
										className="flex items-center w-full sm:w-auto"
									>
										{showCompleted ? (
											<EyeOff className="h-4 w-4 mr-1" />
										) : (
											<Eye className="h-4 w-4 mr-1" />
										)}
										{showCompleted ? "Hide" : "Show"} Completed (
										{completedCount})
									</Button>
									<Button
										variant={showArchived ? "default" : "outline"}
										size="sm"
										onClick={() => setShowArchived(!showArchived)}
										className="flex items-center w-full sm:w-auto"
									>
										{showArchived ? (
											<EyeOff className="h-4 w-4 mr-1" />
										) : (
											<Eye className="h-4 w-4 mr-1" />
										)}
										{showArchived ? "Hide" : "Show"} Archived ({archivedCount})
									</Button>
								</div>
							</div>
							<div className="text-sm text-muted-foreground text-center sm:text-left">
								Showing{" "}
								{visibleCounts.inProgress +
									visibleCounts.notStarted +
									visibleCounts.completed}{" "}
								of {groupedActions.length} actions
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Actions list - To-Do List Style */}
				{topPriorityAction || groupedActions.length > 0 ? (
					<div className="space-y-4">
						{/* Top Priority Section */}
						{topPriorityAction && (
							<div className="space-y-2">
								<div className="flex items-center gap-2 px-3 py-2 rounded-lg border-2 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800">
									<Target className="h-5 w-5 text-purple-600 dark:text-purple-400" />
									<h2 className="font-semibold text-base sm:text-lg">
										Your top priority
									</h2>
								</div>
								<div
									className="space-y-2"
									onDragOver={handleTopPriorityDragOver}
									onDragLeave={handleTopPriorityDragLeave}
									onDrop={handleTopPriorityDrop}
								>
									{renderAction(topPriorityAction, true)}
								</div>
							</div>
						)}

						{(() => {
							// Group actions for display with headers
							const inProgressGroup = groupedActions.filter(
								(a) => a.status === "in_progress",
							);
							const notStartedGroup = groupedActions.filter(
								(a) => a.status === "not_started",
							);
							const completedGroup = groupedActions.filter(
								(a) => a.status === "completed",
							);

							// Render groups with headers
							const renderGroup = (
								title: string,
								actions: CorrectiveAction[],
								icon: React.ReactNode,
								color: string,
								sectionKey: "inProgress" | "notStarted" | "completed",
							) => {
								if (actions.length === 0) return null;
								const visibleCount = visibleCounts[sectionKey];
								const visibleInGroup = actions.slice(0, visibleCount);
								const remainingCount = actions.length - visibleCount;
								const hasMore = remainingCount > 0;

								return (
									<div key={title} className="space-y-2">
										<div
											className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 ${color}`}
										>
											{icon}
											<h2 className="font-semibold text-sm sm:text-base">
												{title}
											</h2>
											<Badge variant="outline" className="ml-auto text-xs">
												{actions.length}
											</Badge>
										</div>
										<div className="space-y-2">
											{visibleInGroup.map((action) =>
												renderAction(action, false),
											)}
										</div>
										{hasMore && (
											<div className="pt-2">
												<Button
													variant="outline"
													onClick={() => {
														setVisibleCounts((prev) => ({
															...prev,
															[sectionKey]: prev[sectionKey] + 3,
														}));
													}}
													className="flex items-center w-full sm:w-auto"
												>
													<ChevronDown className="h-4 w-4 mr-1" />
													Load More ({remainingCount} remaining)
												</Button>
											</div>
										)}
									</div>
								);
							};

							return (
								<>
									{renderGroup(
										"In Progress",
										inProgressGroup,
										<PlayCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />,
										"bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800",
										"inProgress",
									)}
									{renderGroup(
										"Not Started",
										notStartedGroup,
										<Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />,
										"bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800",
										"notStarted",
									)}
									{renderGroup(
										"Completed",
										completedGroup,
										<CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />,
										"bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800",
										"completed",
									)}
								</>
							);
						})()}
					</div>
				) : (
					<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
						<CardContent className="text-center py-12">
							<div className="p-3 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 w-fit mx-auto mb-4">
								<Target className="h-8 w-8 text-white" />
							</div>
							<h3 className="text-lg font-semibold mb-2">
								No Corrective Actions Found
							</h3>
							<p className="text-muted-foreground mb-6">
								{correctiveActions.length === 0
									? "Complete some reflections to generate corrective actions that will help you improve."
									: "No actions match your current filters. Try adjusting the filter options above."}
							</p>
							{correctiveActions.length === 0 ? (
								<Button
									asChild
									className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
								>
									<Link to="/reflection/new">Create Your First Reflection</Link>
								</Button>
							) : (
								<Button
									onClick={() => {
										setShowCompleted(true);
										setShowArchived(true);
									}}
									className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
								>
									Show All Actions
								</Button>
							)}
						</CardContent>
					</Card>
				)}

				{/* Create New Action Dialog */}
				<AlertDialog open={showCreateForm} onOpenChange={setShowCreateForm}>
					<AlertDialogContent className="max-w-2xl">
						<AlertDialogHeader>
							<AlertDialogTitle>Create New Action</AlertDialogTitle>
							<AlertDialogDescription>
								Create a new corrective action and assign it to one or more
								active reflections.
							</AlertDialogDescription>
						</AlertDialogHeader>

						<div className="space-y-4">
							<div>
								<Label htmlFor="title">Action Title</Label>
								<Input
									id="title"
									value={newActionTitle}
									onChange={(e) => setNewActionTitle(e.target.value)}
									placeholder="Enter action title..."
									className="mt-1"
								/>
							</div>

							<div>
								<Label htmlFor="description">Description</Label>
								<Input
									id="description"
									value={newActionDescription}
									onChange={(e) => setNewActionDescription(e.target.value)}
									placeholder="Describe the action in detail..."
									className="mt-1"
								/>
							</div>

							<div>
								<Label>Assign to Reflections</Label>
								<p className="text-sm text-muted-foreground mb-2">
									Select one or more active reflections to assign this action
									to:
								</p>
								<div className="space-y-2 max-h-40 overflow-y-auto">
									{activeReflections.length > 0 ? (
										activeReflections.map((reflection) => (
											<div
												key={reflection.id}
												className={`p-3 border rounded-lg cursor-pointer transition-colors ${selectedReflectionIds.includes(reflection.id) ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800" : "hover:bg-gray-50 dark:hover:bg-gray-800/50"}`}
												onClick={() => toggleReflectionSelection(reflection.id)}
											>
												<div className="flex items-center justify-between">
													<div>
														<h4 className="font-medium text-sm">
															{reflection.title}
														</h4>
														<p className="text-xs text-muted-foreground">
															{reflection.description?.slice(0, 100)}...
														</p>
													</div>
													<div
														className={`w-4 h-4 rounded border-2 ${selectedReflectionIds.includes(reflection.id) ? "bg-blue-600 dark:bg-blue-500 border-blue-600 dark:border-blue-500" : "border-gray-300 dark:border-gray-600"}`}
													>
														{selectedReflectionIds.includes(reflection.id) && (
															<CheckCircle className="w-4 h-4 text-white" />
														)}
													</div>
												</div>
											</div>
										))
									) : (
										<p className="text-sm text-muted-foreground py-4">
											No active reflections found.
											<Link
												to="/reflection/new"
												className="text-blue-600 hover:underline ml-1"
											>
												Create a reflection first
											</Link>
										</p>
									)}
								</div>
							</div>
						</div>

						<AlertDialogFooter>
							<AlertDialogCancel>Cancel</AlertDialogCancel>
							<AlertDialogAction
								onClick={submitCreateAction}
								disabled={
									!newActionTitle.trim() ||
									selectedReflectionIds.length === 0 ||
									updating
								}
							>
								{updating ? "Creating..." : "Create Action"}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>

				<Dialog
					open={!!openForAction}
					onOpenChange={(o) => {
						if (!o) setOpenForAction(null);
					}}
				>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Set Calendar Reminder</DialogTitle>
							<DialogDescription>
								Create an event for this corrective action and add it to your
								calendar.
							</DialogDescription>
						</DialogHeader>

						{selectedAction && (
							<div className="space-y-4">
								<div>
									<Label className="mb-1 block">Event title</Label>
									<Input
										value={eventTitle}
										onChange={(e) => setEventTitle(e.target.value)}
									/>
									<p className="text-xs text-muted-foreground mt-1">
										Prefilled from the action title. You can edit it.
									</p>
								</div>

								<div>
									<Label className="mb-1 block">Event type</Label>
									<_Select
										value={eventType}
										onValueChange={(v) => setEventType(v as any)}
									>
										<_SelectTrigger className="w-full">
											<_SelectValue />
										</_SelectTrigger>
										<_SelectContent>
											<_SelectItem value="one-off">One-off</_SelectItem>
											<_SelectItem value="recurring">Recurring</_SelectItem>
										</_SelectContent>
									</_Select>
								</div>

								{eventType === "recurring" && (
									<div>
										<Label className="mb-1 block">Recurrence</Label>
										<_Select
											value={recurrenceType}
											onValueChange={(v) => setRecurrenceType(v as any)}
										>
											<_SelectTrigger className="w-full">
												<_SelectValue />
											</_SelectTrigger>
											<_SelectContent>
												<_SelectItem value="daily">Every day</_SelectItem>
												<_SelectItem value="weekly">Every week</_SelectItem>
											</_SelectContent>
										</_Select>
									</div>
								)}

								{eventType === "recurring" && recurrenceType === "weekly" && (
									<div>
										<Label className="mb-1 block">Day of week</Label>
										<_Select
											value={String(weeklyDay)}
											onValueChange={(v) => setWeeklyDay(Number(v))}
										>
											<_SelectTrigger className="w-full">
												<_SelectValue />
											</_SelectTrigger>
											<_SelectContent>
												<_SelectItem value="0">Sunday</_SelectItem>
												<_SelectItem value="1">Monday</_SelectItem>
												<_SelectItem value="2">Tuesday</_SelectItem>
												<_SelectItem value="3">Wednesday</_SelectItem>
												<_SelectItem value="4">Thursday</_SelectItem>
												<_SelectItem value="5">Friday</_SelectItem>
												<_SelectItem value="6">Saturday</_SelectItem>
											</_SelectContent>
										</_Select>
									</div>
								)}

								<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
									<div>
										<Label className="mb-1 block">Date</Label>
										<Input
											type="date"
											value={date}
											onChange={(e) => setDate(e.target.value)}
										/>
									</div>
									<div>
										<Label className="mb-1 block">Time</Label>
										<Input
											type="time"
											value={time}
											onChange={(e) => setTime(e.target.value)}
										/>
									</div>
									<div>
										<Label className="mb-1 block">Duration (mins)</Label>
										<Input
											type="number"
											min={5}
											step={5}
											value={duration}
											onChange={(e) =>
												setDuration(parseInt(e.target.value) || 30)
											}
										/>
									</div>
								</div>

								<div className="space-y-2">
									<Label className="mb-1 block">Add to calendar</Label>
									<div className="flex flex-wrap gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => {
												const [hh, mm] = time.split(":");
												const startLocal = new Date(`${date}T${hh}:${mm}:00`);
												const endLocal = new Date(
													startLocal.getTime() + duration * 60000,
												);
												const toUTC = (d: Date) =>
													`${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, "0")}${String(d.getUTCDate()).padStart(2, "0")}T${String(d.getUTCHours()).padStart(2, "0")}${String(d.getUTCMinutes()).padStart(2, "0")}${String(d.getUTCSeconds()).padStart(2, "0")}Z`;
												const dates = `${toUTC(startLocal)}/${toUTC(endLocal)}`;
												const text = encodeURIComponent(
													eventTitle || selectedAction.title,
												);
												const details = encodeURIComponent(
													`${selectedAction.description}\n${window.location.origin}/reflection/${selectedAction.reflection_id}`,
												);
												const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&details=${details}&dates=${dates}`;
												window.open(url, "_blank");
											}}
											disabled={eventType === "recurring"}
											title={
												eventType === "recurring"
													? "Use iCal for recurring events"
													: "Add one-off event to Google Calendar"
											}
										>
											Google Calendar
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() => {
												const [hh, mm] = time.split(":");
												const startLocal = new Date(`${date}T${hh}:${mm}:00`);
												const endLocal = new Date(
													startLocal.getTime() + duration * 60000,
												);
												const subject = encodeURIComponent(
													eventTitle || selectedAction.title,
												);
												const body = encodeURIComponent(
													`${selectedAction.description}\n${window.location.origin}/reflection/${selectedAction.reflection_id}`,
												);
												const startdt = encodeURIComponent(
													startLocal.toISOString(),
												);
												const enddt = encodeURIComponent(
													endLocal.toISOString(),
												);
												const url = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${subject}&body=${body}&startdt=${startdt}&enddt=${enddt}&allday=false&path=/calendar/action/compose`;
												window.open(url, "_blank");
											}}
											disabled={eventType === "recurring"}
											title={
												eventType === "recurring"
													? "Use iCal for recurring events"
													: "Add one-off event to Outlook"
											}
										>
											Outlook
										</Button>
										<Button
											variant="default"
											size="sm"
											onClick={() => {
												const [hh, mm] = time.split(":");
												const startLocal = new Date(`${date}T${hh}:${mm}:00`);
												const endLocal = new Date(
													startLocal.getTime() + duration * 60000,
												);
												const pad = (n: number) => String(n).padStart(2, "0");
												const toUTC = (d: Date) =>
													`${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
												const escapeText = (s: string) =>
													(s || "")
														.replace(/\\/g, "\\\\")
														.replace(/\n/g, "\\n")
														.replace(/,/g, "\\,")
														.replace(/;/g, "\\;");
												const uid = `${crypto.randomUUID()}@reflection-journal`;
												const lines = [
													"BEGIN:VCALENDAR",
													"VERSION:2.0",
													"PRODID:-//Reflection Journal//EN",
													"CALSCALE:GREGORIAN",
													"BEGIN:VEVENT",
													`UID:${uid}`,
													`DTSTAMP:${toUTC(new Date())}`,
													`DTSTART:${toUTC(startLocal)}`,
													`DTEND:${toUTC(endLocal)}`,
													`SUMMARY:${escapeText(eventTitle || selectedAction.title)}`,
													`DESCRIPTION:${escapeText(selectedAction.description)}\\n${window.location.origin}/reflection/${selectedAction.reflection_id}`,
													`URL:${window.location.origin}/reflection/${selectedAction.reflection_id}`,
												];
												if (eventType === "recurring") {
													if (recurrenceType === "daily")
														lines.push("RRULE:FREQ=DAILY");
													if (recurrenceType === "weekly") {
														const byDay = [
															"SU",
															"MO",
															"TU",
															"WE",
															"TH",
															"FR",
															"SA",
														][weeklyDay];
														lines.push(`RRULE:FREQ=WEEKLY;BYDAY=${byDay}`);
													}
												}
												lines.push("END:VEVENT", "END:VCALENDAR");
												const ics = lines.join("\r\n");
												const blob = new Blob([ics], {
													type: "text/calendar;charset=utf-8",
												});
												const url = URL.createObjectURL(blob);
												const a = document.createElement("a");
												a.href = url;
												a.download = `${(eventTitle || selectedAction.title).replace(/[^a-z0-9-_]+/gi, "_")}.ics`;
												document.body.appendChild(a);
												a.click();
												document.body.removeChild(a);
												URL.revokeObjectURL(url);
											}}
										>
											Download iCal (.ics)
										</Button>
									</div>
									<p className="text-xs text-muted-foreground">
										Description includes what to do and a link back to the
										reflection.
										{eventType === "recurring"
											? " Recurring events are provided via iCal (.ics) for broad compatibility (Google, Outlook, Apple)."
											: ""}
									</p>
								</div>
							</div>
						)}

						<DialogFooter>
							<Button variant="outline" onClick={() => setOpenForAction(null)}>
								Close
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>
		</div>
	);
}
